import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { CalendarClock, History } from "lucide-react";

export function PacketShareHistory({ customerId, refreshKey }) {
  const [shares, setShares] = useState([]);
  useEffect(() => {
    api.get(`/customers/${customerId}/packet/shares`).then(({ data }) => setShares(data)).catch((err) => toast.error(formatApiError(err)));
  }, [customerId, refreshKey]);
  return (
    <Card className="overflow-hidden border-t-4 border-t-indigo-500" data-testid="packet-share-history">
      <div className="px-5 py-3 border-b border-indigo-100 bg-indigo-50/60 flex items-center gap-2 font-semibold text-slate-900"><History className="h-4 w-4 text-indigo-700" />Packet share history<span className="text-xs font-mono text-slate-500">({shares.length})</span></div>
      <Table>
        <TableHeader><TableRow><TableHead>Sent</TableHead><TableHead>To</TableHead><TableHead>Sent by</TableHead><TableHead>Note</TableHead></TableRow></TableHeader>
        <TableBody>
          {shares.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-slate-500" data-testid="packet-share-empty">Packet has not been emailed yet.</TableCell></TableRow>}
          {shares.map((s) => (
            <TableRow key={s.id} data-testid={`packet-share-row-${s.id}`}>
              <TableCell className="font-mono text-xs whitespace-nowrap">{new Date(s.sent_at).toLocaleString()}</TableCell>
              <TableCell><div className="font-medium">{s.agent_name || "—"}</div><div className="font-mono text-xs text-slate-500">{s.to}</div></TableCell>
              <TableCell className="text-sm">{s.sent_by_name}{s.scheduled && <Badge className="ml-2 bg-blue-50 text-blue-700 border border-blue-200">Scheduled</Badge>}{s.kind === "hazmat_binder" && <Badge className="ml-2 bg-orange-50 text-orange-800 border border-orange-300">Hazmat binder</Badge>}</TableCell>
              <TableCell className="text-sm text-slate-600 max-w-[320px] truncate" title={s.note || ""}>{s.note || "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

const EMPTY = { frequency: "off", to: "", agent_name: "", renewal_date: "", lead_days: 30, cc_customer: true };

export function PacketSchedulePanel({ customerId }) {
  const [form, setForm] = useState(EMPTY);
  const [nextSend, setNextSend] = useState(null);
  const [busy, setBusy] = useState(false);
  const apply = useCallback((d) => { setForm({ ...EMPTY, ...d, to: d.to || "", agent_name: d.agent_name || "", renewal_date: d.renewal_date || "" }); setNextSend(d.next_send); }, []);
  useEffect(() => {
    api.get(`/customers/${customerId}/packet/schedule`).then(({ data }) => apply(data)).catch((err) => toast.error(formatApiError(err)));
  }, [customerId, apply]);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.put(`/customers/${customerId}/packet/schedule`, { ...form, lead_days: Number(form.lead_days), origin_url: window.location.origin });
      apply(data); toast.success(data.frequency === "off" ? "Scheduled packet turned off" : `Schedule saved · next send ${data.next_send}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Card className="overflow-hidden border-t-4 border-t-teal-500" data-testid="packet-schedule-panel">
      <div className="px-5 py-3 border-b border-teal-100 bg-teal-50/60 flex items-center justify-between">
        <div className="flex items-center gap-2 font-semibold text-slate-900"><CalendarClock className="h-4 w-4 text-teal-700" />Scheduled packet</div>
        {form.frequency !== "off" && nextSend && <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono" data-testid="packet-schedule-next">Next send {nextSend}</Badge>}
        {form.frequency === "off" && <Badge className="bg-slate-100 text-slate-600 border border-slate-200" data-testid="packet-schedule-off">Off</Badge>}
      </div>
      <form onSubmit={save} className="p-5 space-y-4">
        <p className="text-sm text-slate-600">Automatically email the latest packet to your insurance agent. Quarterly sends go out on Jan 1, Apr 1, Jul 1 and Oct 1.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <div><Label>Frequency</Label>
            <Select value={form.frequency} onValueChange={set("frequency")}>
              <SelectTrigger data-testid="packet-schedule-frequency"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="off">Off</SelectItem><SelectItem value="quarterly">Quarterly</SelectItem><SelectItem value="renewal">Before policy renewal</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Agent email</Label><Input type="email" required={form.frequency !== "off"} value={form.to} onChange={(e) => set("to")(e.target.value)} data-testid="packet-schedule-to-input" /></div>
          <div><Label>Agent name (optional)</Label><Input value={form.agent_name} onChange={(e) => set("agent_name")(e.target.value)} data-testid="packet-schedule-name-input" /></div>
          {form.frequency === "renewal" && (<>
            <div><Label>Policy renewal date</Label><Input type="date" required value={form.renewal_date} onChange={(e) => set("renewal_date")(e.target.value)} data-testid="packet-schedule-renewal-input" /></div>
            <div><Label>Days before renewal</Label><Input type="number" min={1} max={120} value={form.lead_days} onChange={(e) => set("lead_days")(e.target.value)} data-testid="packet-schedule-lead-input" /></div>
          </>)}
        </div>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-700"><Switch checked={form.cc_customer} onCheckedChange={set("cc_customer")} data-testid="packet-schedule-cc-switch" />Send me a copy each time</label>
          <Button type="submit" disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="packet-schedule-save-button">{busy ? "Saving…" : "Save schedule"}</Button>
        </div>
      </form>
    </Card>
  );
}
